"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { pushToDataLayer } from "@/components/analytics";
import { Button } from "@/components/ui/Button";
import { CONSENT_EVENT, STORAGE_KEY } from "./constants";
import styles from "./CookieConsent.module.css";

/**
 * Cookie consent types
 */
export type ConsentType = "analytics" | "marketing" | "personalization";

export interface ConsentState {
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
  necessary: true; // Always true, cannot be disabled
}

interface CookieConsentProps {
  locale?: "en" | "es";
}


/**
 * Translations for the cookie banner
 */
const translations = {
  en: {
    title: "Cookie Preferences",
    description:
      "We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. You can choose which cookies to allow.",
    acceptAll: "Accept All",
    rejectAll: "Reject All",
    customize: "Customize",
    savePreferences: "Save Preferences",
    necessary: "Necessary",
    necessaryDesc: "Required for the website to function properly.",
    analytics: "Analytics",
    analyticsDesc: "Help us understand how visitors interact with our website.",
    marketing: "Marketing",
    marketingDesc: "Used to deliver relevant advertisements.",
    personalization: "Personalization",
    personalizationDesc: "Allow us to remember your preferences.",
    learnMore: "Learn more in our",
    cookiePolicy: "Cookie Policy",
  },
  es: {
    title: "Preferencias de Cookies",
    description:
      "Utilizamos cookies para mejorar tu experiencia de navegación, analizar el tráfico del sitio y personalizar el contenido. Puedes elegir qué cookies permitir.",
    acceptAll: "Aceptar Todo",
    rejectAll: "Rechazar Todo",
    customize: "Personalizar",
    savePreferences: "Guardar Preferencias",
    necessary: "Necesarias",
    necessaryDesc: "Requeridas para el funcionamiento del sitio web.",
    analytics: "Analíticas",
    analyticsDesc: "Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio.",
    marketing: "Marketing",
    marketingDesc: "Utilizadas para mostrar anuncios relevantes.",
    personalization: "Personalización",
    personalizationDesc: "Nos permiten recordar tus preferencias.",
    learnMore: "Más información en nuestra",
    cookiePolicy: "Política de Cookies",
  },
};

/**
 * Update GTM Consent Mode with current consent state
 * Uses gtag() which is defined in root layout before GTM loads
 */
function updateGTMConsent(consent: ConsentState): void {
  if (typeof window === "undefined") return;

  // Ensure dataLayer exists (should be defined in root layout)
  window.dataLayer = window.dataLayer || [];

  // Use gtag if available (defined in root layout inline script)
  // This updates consent in the format GTM Consent Mode v2 expects
  if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: consent.analytics ? "granted" : "denied",
      ad_storage: consent.marketing ? "granted" : "denied",
      ad_user_data: consent.marketing ? "granted" : "denied",
      ad_personalization: consent.marketing ? "granted" : "denied",
      personalization_storage: consent.personalization ? "granted" : "denied",
    });
  }

  // Tools loaded outside GTM (Microsoft Clarity) listen for this
  window.dispatchEvent(new CustomEvent<ConsentState>(CONSENT_EVENT, { detail: consent }));

  // Also push a custom event for GTM triggers
  pushToDataLayer({
    event: "consent_update",
    consent_analytics: consent.analytics ? "granted" : "denied",
    consent_marketing: consent.marketing ? "granted" : "denied",
    consent_personalization: consent.personalization ? "granted" : "denied",
  });
}

/**
 * Set default consent state (denied until user accepts)
 * Note: Default consent is now set in root layout.tsx before GTM loads.
 * This function is kept for backwards compatibility but consent defaults
 * are handled by the inline script in layout.tsx for proper timing.
 */
export function setDefaultConsent(): void {
  if (typeof window === "undefined") return;

  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];

  // Push a custom event for GTM triggers (defaults are set in root layout)
  window.dataLayer.push({
    event: "consent_default",
    consent_analytics: "denied",
    consent_marketing: "denied",
    consent_personalization: "denied",
  });
}

/**
 * Cookie Consent Banner Component
 *
 * Features:
 * - GTM Consent Mode v2 integration
 * - Accept All / Reject All / Customize options
 * - Persists preferences to localStorage
 * - i18n support (English/Spanish)
 * - Accessible (keyboard navigation, ARIA labels)
 * - Matches design system
 */
export function CookieConsent({ locale = "en" }: CookieConsentProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [consent, setConsent] = useState<ConsentState>({
    analytics: false,
    marketing: false,
    personalization: false,
    necessary: true,
  });

  const t = translations[locale];

  // Check if consent has already been given
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ConsentState;
        setConsent(parsed);
        updateGTMConsent(parsed);
      } catch {
        // Invalid stored value, show banner
        setIsVisible(true);
        setDefaultConsent();
      }
    } else {
      // No stored consent, show banner
      setIsVisible(true);
      setDefaultConsent();
    }
  }, []);

  const saveConsent = (newConsent: ConsentState) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newConsent));
    setConsent(newConsent);
    updateGTMConsent(newConsent);
    setIsVisible(false);
    setShowCustomize(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      analytics: true,
      marketing: true,
      personalization: true,
      necessary: true,
    });
  };

  const handleRejectAll = () => {
    saveConsent({
      analytics: false,
      marketing: false,
      personalization: false,
      necessary: true,
    });
  };

  const handleSavePreferences = () => {
    saveConsent(consent);
  };

  const toggleConsent = (type: ConsentType) => {
    setConsent((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (!isVisible) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-labelledby="cookie-title" aria-modal="true">
      <div className={styles.banner}>
        <div className={styles.content}>
          <h2 id="cookie-title" className={styles.title}>
            {t.title}
          </h2>
          <p className={styles.description}>{t.description}</p>

          {showCustomize ? (
            <div className={styles.options}>
              {/* Necessary - Always enabled */}
              <label className={styles.option} htmlFor="cookie-necessary">
                <div className={styles.optionHeader}>
                  <input
                    id="cookie-necessary"
                    type="checkbox"
                    checked={true}
                    disabled
                    className={styles.checkbox}
                  />
                  <span className={styles.optionTitle}>{t.necessary}</span>
                </div>
                <span className={styles.optionDesc}>{t.necessaryDesc}</span>
              </label>

              {/* Analytics */}
              <label className={styles.option} htmlFor="cookie-analytics">
                <div className={styles.optionHeader}>
                  <input
                    id="cookie-analytics"
                    type="checkbox"
                    checked={consent.analytics}
                    onChange={() => toggleConsent("analytics")}
                    className={styles.checkbox}
                  />
                  <span className={styles.optionTitle}>{t.analytics}</span>
                </div>
                <span className={styles.optionDesc}>{t.analyticsDesc}</span>
              </label>

              {/* Marketing */}
              <label className={styles.option} htmlFor="cookie-marketing">
                <div className={styles.optionHeader}>
                  <input
                    id="cookie-marketing"
                    type="checkbox"
                    checked={consent.marketing}
                    onChange={() => toggleConsent("marketing")}
                    className={styles.checkbox}
                  />
                  <span className={styles.optionTitle}>{t.marketing}</span>
                </div>
                <span className={styles.optionDesc}>{t.marketingDesc}</span>
              </label>

              {/* Personalization */}
              <label className={styles.option} htmlFor="cookie-personalization">
                <div className={styles.optionHeader}>
                  <input
                    id="cookie-personalization"
                    type="checkbox"
                    checked={consent.personalization}
                    onChange={() => toggleConsent("personalization")}
                    className={styles.checkbox}
                  />
                  <span className={styles.optionTitle}>{t.personalization}</span>
                </div>
                <span className={styles.optionDesc}>{t.personalizationDesc}</span>
              </label>
            </div>
          ) : null}

          <p className={styles.learnMore}>
            {t.learnMore}{" "}
            <Link href={`/${locale}/cookies`} className={styles.link}>
              {t.cookiePolicy}
            </Link>
          </p>
        </div>

        <div className={styles.actions}>
          {showCustomize ? (
            <Button onClick={handleSavePreferences} variant="default">
              {t.savePreferences}
            </Button>
          ) : (
            <>
              <Button onClick={handleAcceptAll} variant="default">
                {t.acceptAll}
              </Button>
              <Button onClick={() => setShowCustomize(true)} variant="outline">
                {t.customize}
              </Button>
              <Button onClick={handleRejectAll} variant="ghost">
                {t.rejectAll}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to get current consent state
 */
export function useConsent(): ConsentState | null {
  const [consent, setConsent] = useState<ConsentState | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setConsent(JSON.parse(stored));
      } catch {
        setConsent(null);
      }
    }
  }, []);

  return consent;
}

/**
 * Function to reset consent (for testing or user request)
 */
export function resetConsent(): void {
  localStorage.removeItem(STORAGE_KEY);
  window.location.reload();
}
